import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Center,
  useToast,
  Image,
  VStack,
  Checkbox,
} from "@chakra-ui/react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Link as ChakraLink } from "@chakra-ui/react";

import { AUTH_PAGES } from "../../../base/router/pages";

import logo from "../../../assets/images/logo2.png";
import { useValidEmail } from "../../../hooks/useValidEmail";
import { useValidName } from "../../../hooks/useValidName";
import { useValidLastName } from "../../../hooks/useValidLastName";
import {
  useCheckEmail,
  useGetAuthUser,
  useSetAuthUser,
  useVerifyPhone,
} from "../../../api/AuthApi";
import { useValidPhoneMask } from "../../../hooks/useValidPhoneMask";
import PageContainer from "../../../components/PageContainer";
import { THEME_COLORS } from "../../../base/theme/foundations/colors";

const RegisterPage = (): JSX.Element => {
  useGetAuthUser();
  const { email, setEmail, emailIsValid } = useValidEmail(null);
  const { firstName, setFirstName, nameIsValid } = useValidName(null);
  const { lastName, setLastName, lastNameIsValid } = useValidLastName(null);
  const [providerId, setProviderId] = useState("");
  const [confirmProviderId, setConfirmProviderId] = useState("");
  const [confirmAgreement, setConfirmAggreement] = useState(false);
  const { ref, phone, invalidPhoneError, parsedPhone } = useValidPhoneMask("");
  const { mutateAsync: setAuthUser } = useSetAuthUser();
  const { mutateAsync: checkEmail, isPending: isCheckingEmail } = useCheckEmail();
  const { mutateAsync: verifyPhone, isPending: isVerifyingPhone } = useVerifyPhone();
  const navigate = useNavigate();
  const toast = useToast();

  const onNextClicked = () => {
    if (!email) return toast({ description: "Please enter email", status: "error" });
    checkEmail(email).then(async () => {
      await setAuthUser({
        email,
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        phone: parsedPhone ?? "",
        providerId,
      });
      await verifyPhone({
        phone: parsedPhone ?? "",
        email,
      });
      navigate(AUTH_PAGES.verifyPhone);
    });
  };

  const isButtonDisabled = () => {
    return (
      !confirmAgreement ||
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !providerId ||
      !confirmProviderId ||
      providerId !== confirmProviderId ||
      invalidPhoneError !== "" ||
      emailIsValid !== "" ||
      nameIsValid !== "" ||
      lastNameIsValid !== ""
    );
  };

  return (
    <PageContainer variant="centered-auth">
      <VStack minH="100%">
        <Image src={logo} alt="LnQ" width="160px" />
        <Card width={488} marginTop={30}>
          <CardBody px={"60px"} borderRadius={"20px"}>
            <Text
              fontSize="24px"
              fontWeight={700}
              align="center"
              mt={2}
              color="brandBlue.800"
            >
              Create an account
            </Text>
            <Text align="center" color={THEME_COLORS.gray[600]}>
              Please fill in all required fields to register
            </Text>
            <form>
              <Stack spacing={4} mt={8}>
                <FormControl variant="floating">
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="First Name"
                    name="firstName"
                    type="text"
                    autoComplete="First name"
                    required
                    value={firstName ?? ""}
                    onChange={(e) => setFirstName(e.target.value)}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                    isInvalid={nameIsValid !== ""}
                  />
                  <FormLabel>First Name</FormLabel>
                </FormControl>

                <FormControl variant="floating">
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="Last name"
                    name="lastName"
                    type="text"
                    autoComplete="Last name"
                    required
                    value={lastName ?? ""}
                    onChange={(e) => setLastName(e.target.value)}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                    isInvalid={lastNameIsValid !== ""}
                  />
                  <FormLabel>Last name</FormLabel>
                </FormControl>

                <FormControl variant="floating">
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="Email"
                    name="email"
                    type="email"
                    autoComplete="password"
                    required
                    value={email ?? ""}
                    onChange={(e) => setEmail(e.target.value)}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                    isInvalid={emailIsValid !== ""}
                  />
                  <FormLabel>Email</FormLabel>
                </FormControl>
                <FormControl variant="floating">
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="Phone"
                    name="phone"
                    type="tel"
                    required
                    inputMode="tel"
                    value={phone}
                    ref={ref as React.RefObject<HTMLInputElement>}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                    isInvalid={invalidPhoneError !== ""}
                    onChange={(e) => {
                      e.preventDefault();
                    }}
                  />
                  <FormLabel>Phone</FormLabel>
                </FormControl>
                <FormControl variant="floating">
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="NPI Number"
                    name="providerId"
                    type="number"
                    required
                    value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                  />
                  <FormLabel>NPI Number</FormLabel>
                </FormControl>
                <FormControl
                  variant="floating"
                  isInvalid={
                    Boolean(providerId) &&
                    Boolean(confirmProviderId) &&
                    providerId !== confirmProviderId
                  }
                >
                  <Input
                    placeholder="" // leave empty for floating label
                    aria-label="Confirm NPI Number"
                    name="confirmProviderId"
                    type="number"
                    required
                    value={confirmProviderId}
                    onChange={(e) => setConfirmProviderId(e.target.value)}
                    isDisabled={isCheckingEmail || isVerifyingPhone}
                  />
                  <FormLabel>Confirm NPI Number</FormLabel>
                </FormControl>
                <Checkbox
                  colorScheme="brandYellow"
                  size="lg"
                  isChecked={confirmAgreement}
                  onChange={() => setConfirmAggreement(!confirmAgreement)}
                >
                  <Text fontSize={"12px"} color={THEME_COLORS.gray[600]}>
                    I agree to Terms of Service and Privacy Policy
                  </Text>
                </Checkbox>
                <Button
                  my={4}
                  backgroundColor={THEME_COLORS.brandYellow[500]}
                  textColor={THEME_COLORS.brandBlue[800]}
                  isLoading={isCheckingEmail || isVerifyingPhone}
                  isDisabled={isButtonDisabled()}
                  onClick={onNextClicked}
                >
                  Continue
                </Button>
                <Center flexWrap="wrap">
                  <Text fontSize="sm" color="gray.500" me={1}>
                    Have an account?
                  </Text>
                  <ChakraLink
                    as={Link}
                    to={AUTH_PAGES.login}
                    color="brandBlue.800"
                    fontWeight="medium"
                    fontSize="sm"
                    _hover={{ color: "gray.900" }}
                  >
                    Login
                  </ChakraLink>
                </Center>
              </Stack>
            </form>
          </CardBody>
        </Card>
      </VStack>
    </PageContainer>
  );
};

export default RegisterPage;
